import { Minus, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';

import { Field } from '@/components/ui/admin/field';
import { cn } from '@/lib/utils';
import { type CommonFormFieldProps, spanToClassName } from './form-input';

type FormNumberInputProps<T extends FieldValues = FieldValues> =
  CommonFormFieldProps<T> & {
    placeholder?: string;
    autoComplete?: string;
    step?: number;
    min?: number;
    max?: number;
    decimalScale?: number;
    showControls?: boolean;
  } & Omit<React.ComponentProps<'input'>, 'name'>;

type NumberInputFieldProps = {
  value?: number;
  onChange: (value?: number) => void;
  onBlur: () => void;
  inputRef: (instance: HTMLInputElement | null) => void;
  label: string;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
  decimalScale: number;
  step: number;
  min?: number;
  max?: number;
  showControls: boolean;
  invalid?: boolean;
} & Omit<React.ComponentProps<'input'>, 'name' | 'value' | 'onChange'>;

function NumberInputField({
  value,
  onChange,
  onBlur,
  inputRef,
  label,
  required,
  placeholder,
  autoComplete,
  decimalScale,
  step,
  min,
  max,
  showControls,
  invalid,
  disabled,
  ...props
}: NumberInputFieldProps) {
  const [displayValue, setDisplayValue] = useState(value?.toString() ?? '');

  useEffect(() => {
    if (value === undefined || value === null) {
      setDisplayValue('');
      return;
    }

    setDisplayValue(String(value));
  }, [value]);

  const clampValue = (value: number) => {
    let next = value;

    if (min !== undefined) {
      next = Math.max(min, next);
    }

    if (max !== undefined) {
      next = Math.min(max, next);
    }

    return next;
  };

  const updateValue = (value: number) => {
    const next = clampValue(Number(value.toFixed(decimalScale)));

    onChange(next);
    setDisplayValue(String(next));
  };

  return (
    <Field
      {...props}
      label={label}
      required={required}
      invalid={invalid}
      disabled={disabled}
      ref={inputRef}
      value={displayValue}
      placeholder={placeholder}
      autoComplete={autoComplete}
      aria-invalid={invalid}
      className="text-center"
      leading={
        showControls ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => updateValue((value ?? 0) - step)}
            className="flex size-6 shrink-0 items-center justify-center rounded-[7px] border border-border text-faint hover:bg-raised"
          >
            <Minus className="size-3.5" />
          </button>
        ) : undefined
      }
      trailing={
        showControls ? (
          <button
            type="button"
            disabled={disabled}
            onClick={() => updateValue((value ?? 0) + step)}
            className="flex size-6 shrink-0 items-center justify-center rounded-[7px] border border-border text-faint hover:bg-raised"
          >
            <Plus className="size-3.5" />
          </button>
        ) : undefined
      }
      onBlur={() => {
        const normalized = displayValue.replace(',', '.');

        if (normalized === '' || normalized === '.' || normalized === ',') {
          setDisplayValue('');
          onChange(undefined);
          onBlur();
          return;
        }

        const numberValue = Number(normalized);

        if (!Number.isNaN(numberValue)) {
          const formatted = Number(numberValue.toFixed(decimalScale));

          onChange(formatted);
          setDisplayValue(String(formatted));
        }

        onBlur();
      }}
      onChange={(e) => {
        const value = e.target.value;

        if (!/^\d*[.,]?\d*$/.test(value)) {
          return;
        }

        const separators = (value.match(/[.,]/g) || []).length;

        if (separators > 1) {
          return;
        }

        const normalized = value.replace(',', '.');

        const decimalPart = normalized.split('.')[1];

        if (decimalPart && decimalPart.length > decimalScale) {
          return;
        }

        setDisplayValue(value);

        if (normalized === '' || normalized === '.' || normalized === ',') {
          onChange(undefined);
          return;
        }

        const numericValue = Number(normalized);

        if (!Number.isNaN(numericValue)) {
          onChange(numericValue);
        }
      }}
    />
  );
}

export function FormNumberInput<T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  autoComplete,
  step = 1,
  min,
  max,
  decimalScale = 2,
  showControls = true,
  sm,
  md,
  lg,
  className,
  disabled,
  required,
  ...props
}: FormNumberInputProps<T>) {
  return (
    <Controller
      name={name as FieldPath<T>}
      control={control}
      render={({ field, fieldState }) => (
        <div className={cn(spanToClassName(sm, md, lg), className)}>
          <NumberInputField
            {...props}
            label={label}
            required={required}
            disabled={disabled || field.disabled}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            inputRef={field.ref}
            placeholder={placeholder}
            autoComplete={autoComplete}
            decimalScale={decimalScale}
            step={step}
            min={min}
            max={max}
            showControls={showControls}
            invalid={fieldState.invalid}
          />

          {fieldState.error && (
            <p className="mt-1.5 text-xs font-medium text-destructive">
              {fieldState.error.message}
            </p>
          )}
        </div>
      )}
    />
  );
}
